"use client"
import { useState, useEffect, useRef } from "react"
import styles from "./BlueGallery.module.css"


// Önce/sonra galerisi görsellerini yatay kaydırılabilir bir formatta sergileyen bileşen.
// Sürükle-bırak, ok tuşları ve duyarlı tasarım destekli.

export default function BlueGallery() {
  // Mevcut görüntülenen slayt indeksi
  const [currentIndex, setCurrentIndex] = useState(0)
  // Ekranda aynı anda görünen slayt sayısı
  const [visibleCount, setVisibleCount] = useState(3)
  // Kullanıcının aktif olarak sürükleyip sürüklemediği
  const [isDragging, setIsDragging] = useState(false)
  // Geçiş animasyonunun devam edip etmediği
  const [isAnimating, setIsAnimating] = useState(false)
  // Sürükleme sırasındaki X eksenindeki piksel değişimi
  const [dragX, setDragX] = useState(0)
  // Animasyon sırasındaki geçici offset
  const [offsetX, setOffsetX] = useState(0)
  // Sürükleme başlangıç pozisyonu referansı
  const dragStartXRef = useRef(0)
  // Kaydırıcı track elementi referansı
  const trackRef = useRef(null)
  // Konteyner elementi referansı
  const containerRef = useRef(null)
  // Hesaplanan kutucuk genişliği
  const [tileWidth, setTileWidth] = useState(0)
  // Kutucuklar arası boşluk (piksel)
  const GAP = 16

  // BlueGallery klasöründeki tüm görselleri dinamik olarak içe aktar
  const req = require.context("../../assets/images/BlueGallery", false, /\.(png|jpe?g|webp|gif|svg)$/)
  const items = req.keys().sort().map(req)
  
  // Slayt dizisini oluştur (her görsel için başlık ve kaynak)
  const slides = items.map((src, i) => ({ src, title: `Result ${i + 1}` }))

  // Duyarlı görünen slayt sayısı - maksimum mümkün görüntü sayısını göster
  useEffect(() => {
    const updateVisibleCount = () => {
      const containerWidth = containerRef.current?.getBoundingClientRect().width || window.innerWidth
      const imageWidth = 200 // Temel görüntü genişliği (daha fazla görüntü için azaltıldı)
      const gap = 16
      const maxVisible = Math.floor((containerWidth + gap) / (imageWidth + gap))
      
      if (window.innerWidth <= 480) {
        setVisibleCount(Math.max(1, Math.min(maxVisible, 2)))
      } else if (window.innerWidth <= 768) {
        setVisibleCount(Math.max(2, Math.min(maxVisible, 3)))
      } else if (window.innerWidth <= 1200) {
        setVisibleCount(Math.max(4, Math.min(maxVisible, 5)))
      } else {
        setVisibleCount(Math.max(6, Math.min(maxVisible, 8)))
      }
    }

    updateVisibleCount()
    window.addEventListener("resize", updateVisibleCount)
    return () => window.removeEventListener("resize", updateVisibleCount)
  }, [])

  // Sonraki slayta geç
  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % slides.length)
  }

  // Önceki slayta geç
  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length)
  }

  // Konteyner genişliğine göre kutucuk genişliğini hesapla
  useEffect(() => {
    const calc = () => {
      const el = containerRef.current
      if (!el) return
      const w = el.getBoundingClientRect().width
      const per = (w - (visibleCount - 1) * GAP) / visibleCount
      setTileWidth(per)
    }
    calc()
    window.addEventListener("resize", calc)
    return () => window.removeEventListener("resize", calc)
  }, [visibleCount])

  // Öndeki önceki kutucuğu gizlemek için temel offset
  const baseOffset = -(tileWidth + GAP)

  // Görünür pencerenin slaytlarını al (önceki + görünür + sonraki)
  const getWindowSlides = () => {
    const len = slides.length
    const prevIdx = (currentIndex - 1 + len) % len
    const arr = [slides[prevIdx]]
    for (let i = 0; i < visibleCount; i++) {
      const idx = (currentIndex + i) % len
      arr.push(slides[idx])
    }
    const nextIdx = (currentIndex + visibleCount) % len
    arr.push(slides[nextIdx])
    return arr
  }

  // Sürükleme işleyicileri (işaretçi olayları)
  const onPointerDown = (e) => {
    // Animasyon devam ediyorsa sürüklemeye izin verme
    if (isAnimating) return
    setIsDragging(true)
    setDragX(0)
    dragStartXRef.current = e.clientX ?? (e.touches && e.touches[0] && e.touches[0].clientX) ?? 0
  }

  // Sürükleme sırasında hareket ve bırakma olaylarını dinle
  useEffect(() => {
    if (!isDragging) return
    // Sürükleme hareketi
    const onMove = (e) => {
      const clientX = e.clientX ?? (e.touches && e.touches[0] && e.touches[0].clientX)
      if (clientX == null) return
      setDragX(clientX - dragStartXRef.current)
    }
    // Sürükleme bırakma
    const onUp = () => {
      const threshold = 50
      const track = trackRef.current
      if (!track) return
      let snapDelta = 0
      // Sola sürükleme - sonraki slayta git
      if (dragX <= -threshold) {
        snapDelta = -(tileWidth + GAP)
        setIsAnimating(true)
        track.style.transition = "transform 300ms ease"
        setOffsetX(snapDelta)
        const onEnd = () => {
          track.style.transition = "none"
          nextSlide()
          setOffsetX(0)
          setIsAnimating(false)
          track.removeEventListener("transitionend", onEnd)
        }
        track.addEventListener("transitionend", onEnd)
      // Sağa sürükleme - önceki slayta git
      } else if (dragX >= threshold) {
        snapDelta = tileWidth + GAP
        setIsAnimating(true)
        track.style.transition = "transform 300ms ease"
        setOffsetX(snapDelta)
        const onEnd = () => {
          track.style.transition = "none"
          prevSlide()
          setOffsetX(0)
          setIsAnimating(false)
          track.removeEventListener("transitionend", onEnd)
        }
        track.addEventListener("transitionend", onEnd)
      }

      setIsDragging(false)
      setDragX(0)
    }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    window.addEventListener("touchmove", onMove, { passive: true })
    window.addEventListener("touchend", onUp)
    return () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      window.removeEventListener("touchmove", onMove)
      window.removeEventListener("touchend", onUp)
    }
  }, [isDragging, dragX, tileWidth, GAP])

  return (
    <section className={`section ${styles.blue}`}>
      {/* Arka plan başlığı */}
      <div className={styles.backgroundText}>
        Witness the Power of<br />Transformation!
      </div>
      <div className="container">
        {/* Ana başlık */}
        <h2 className={styles.mainTitle}>
          <span className={styles.titleWhite}>Witness the Power of</span>
          <br />
          <span className={styles.titleTurquoise}>Transformation!</span>
        </h2>
        <div className={styles.sliderWrapper}>
          {/* Sol ok butonu - önceki slayt */}
          <button
            className={`${styles.sliderArrow} ${styles.sliderArrowLeft}`}
            onClick={() => {
              if (isAnimating) return
              const track = trackRef.current
              if (!track) return
              const delta = tileWidth + GAP
              setIsAnimating(true)
              track.style.transition = "transform 300ms ease"
              setOffsetX(delta)
              const onEnd = () => {
                track.style.transition = "none"
                prevSlide()
                setOffsetX(0)
                setIsAnimating(false)
                track.removeEventListener("transitionend", onEnd)
              }
              track.addEventListener("transitionend", onEnd)
            }}
            aria-label="Previous"
          >
            ‹
          </button>
          {/* Kaydırıcı içerik alanı - sürüklenebilir */}
          <div
            ref={containerRef}
            className={`${styles.sliderContent} ${styles[`sliderContent-${visibleCount}`]}`}
            onPointerDown={onPointerDown}
            style={{ cursor: isDragging ? "grabbing" : "grab" }}
          >
            <div
              ref={trackRef}
              className={styles.sliderTrack}
              style={{
                transform: `translateX(${baseOffset + offsetX + dragX}px)`,
              }}
            >
              {getWindowSlides().map((slide, i) => (
                <div
                  className={styles.sliderTile}
                  key={`${currentIndex}-${i}`}
                  style={{
                    width: tileWidth
                      ? `${tileWidth}px`
                      : `calc((100% - ${(visibleCount - 1) * GAP}px) / ${visibleCount})`,
                  }}
                >
                  <img 
                    src={slide.src || "/placeholder.svg"} 
                    alt={slide.title} 
                    className={styles.galleryImg}
                    draggable={false}
                  />
                </div>
              ))}
            </div>
          </div>
          {/* Sağ ok butonu - sonraki slayt */}
          <button
            className={`${styles.sliderArrow} ${styles.sliderArrowRight}`}
            onClick={() => {
              if (isAnimating) return
              const track = trackRef.current
              if (!track) return
              const delta = -(tileWidth + GAP)
              setIsAnimating(true)
              track.style.transition = "transform 300ms ease"
              setOffsetX(delta)
              const onEnd = () => {
                track.style.transition = "none"
                nextSlide()
                setOffsetX(0)
                setIsAnimating(false)
                track.removeEventListener("transitionend", onEnd)
              }
              track.addEventListener("transitionend", onEnd)
            }}
            aria-label="Next"
          >
            ›
          </button>
        </div>
        {/* Tüm sonuçları görüntüleme butonu */}
        <div className="center">
          <a href="/results" className={styles.viewAllBtn}>
            View All Results Gallery
          </a>
        </div>
      </div>
    </section>
  )
}
